import type { CSSProperties, ElementType, ReactNode } from "react";
import styles from "./Typography.module.css";

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
type HeadingSize = "hero" | "2xl" | "xl" | "lg" | "md";
type TextSize = "xs" | "sm" | "base" | "md" | "lg";
type Tone = "default" | "muted" | "faint" | "ghost" | "ruby" | "onLight";
type Weight = "thin" | "light" | "regular" | "medium" | "bold";

interface HeadingProps {
  children: ReactNode;
  level?: HeadingLevel;
  size?: HeadingSize;
  tone?: Tone;
  weight?: Weight;
  align?: "start" | "center" | "end";
  uppercase?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** Display heading. `level` controls the tag (h1..h6), `size` the look. */
export function Heading({
  children,
  level = 2,
  size = "xl",
  tone = "default",
  weight = "light",
  align = "start",
  uppercase,
  className,
  style,
}: HeadingProps) {
  const Tag = `h${level}` as ElementType;
  const cls = [
    styles.heading,
    styles[`size-${size}`],
    styles[`tone-${tone}`],
    styles[`w-${weight}`],
    styles[`align-${align}`],
    uppercase ? styles.uppercase : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <Tag className={cls} style={style}>
      {children}
    </Tag>
  );
}

interface TextProps {
  children: ReactNode;
  size?: TextSize;
  tone?: Tone;
  weight?: Weight;
  align?: "start" | "center" | "end";
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
}

/** Body text. */
export function Text({
  children,
  size = "base",
  tone = "default",
  weight = "light",
  align = "start",
  as: Tag = "p",
  className,
  style,
}: TextProps) {
  const cls = [
    styles.text,
    styles[`size-${size}`],
    styles[`tone-${tone}`],
    styles[`w-${weight}`],
    styles[`align-${align}`],
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <Tag className={cls} style={style}>
      {children}
    </Tag>
  );
}

/** Small uppercase, wide-tracked label above a heading. */
export function Eyebrow({
  children,
  tone = "muted",
  className,
}: {
  children: ReactNode;
  tone?: Tone;
  className?: string;
}) {
  const cls = [styles.eyebrow, styles[`tone-${tone}`], className ?? ""]
    .filter(Boolean)
    .join(" ");
  return <span className={cls}>{children}</span>;
}
